// ITEM 4(c) — HER PHOTO, WHERE HER ORDER PUT IT: MY ROOM, BESIDE THE NAME EDITOR.
//
// The whole control is here rather than in App.tsx for the reason roadmap 7 keeps paying out on: a
// law inside the monolith is held by a regex, and this one has three states a member can be stuck in
// (nothing yet, one she chose, one that failed to send) plus a picker, a crop and a one-time
// pointer. It holds state, so a gate cannot call it — but `squareCropBox`, `avatarRefusal` and
// `prepareAvatarForUpload` are all pure and all live outside it, which is where the arithmetic that
// can actually be wrong lives.
//
// THREE THINGS THIS COMPONENT DOES NOT DO, each on purpose:
//   • It never composes the photo URL. The server hands one back and the control renders it.
//   • It never says anything about moderation. A photo publishes on upload and her seat can put a
//     member back on the house glyph; the member is told nothing, ever, which is LR1's ruling
//     applied to a face.
//   • It never sends the camera's original file. `prepareAvatarForUpload` crops to a centred square
//     and re-encodes before a byte leaves the device, so a 12MP portrait is ~40KB by the time it
//     reaches the route.
import { useEffect, useRef, useState } from "react";
import { prepareAvatarForUpload } from "../photoUpload.js";
import { photoAccept } from "../uploadTypes.js";
import {
  AVATAR_ALT, AVATAR_CHOOSE_LABEL, AVATAR_HELP, AVATAR_POINTER, AVATAR_PHOTO_EDGE,
  AVATAR_REMOVE_LABEL, AVATAR_REPLACE_LABEL, AVATAR_SAVING, AVATAR_UPLOAD_LABEL,
  avatarRefusal,
} from "../livingRoom.js";
import { avatarLoadNote, avatarNote, readAvatar, removeAvatar, saveAvatar } from "../lib/avatar-photo";
import { safeLocalStorage } from "../lib/safe-storage";

const POINTER_KEY = "12h_photo_pointer_seen";

// `pointerReady` is the house's POINTER QUEUE, not a feature flag (SH2's rule, stated in App.tsx:
// one-time pointers take turns, because two of them want the same corner and three glowing boxes
// never land on a member at once). This one is third in line, behind Customize and Shelves.
export function ProfilePhoto({ glyph = "✦", pointerReady = true }: { glyph?: string; pointerReady?: boolean }) {
  const [photo, setPhoto] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const [pointerSeen, setPointerSeen] = useState(() => {
    // SAFE-STORAGE-1: through the one door. **The no-store answer is `true`, and that is the
    // existing catch's own ruling kept exactly** — a device that cannot remember would show this
    // one-time pointer on every single visit, which is worse than never showing it.
    const store = safeLocalStorage();
    return store ? store.getItem(POINTER_KEY) === "1" : true;
  });
  const fileRef = useRef<HTMLInputElement | null>(null);

  // R72 — **`loaded` MEANS THE READ LANDED, AND NOTHING ELSE.** It used to be set inside the success
  // arm on one path and inside a `.catch` on the other, so a REFUSAL left it false forever while the
  // control drew the house glyph over a member who has a photo. Now the answer decides both facts at
  // once: her face on a read that landed, and a sentence saying so on one that did not.
  useEffect(() => {
    let live = true;
    void readAvatar().then((answer) => {
      if (!live) return;
      if (answer.ok) { setPhoto(answer.photo); setLoaded(true); return; }
      setNote(avatarLoadNote(answer));
    });
    return () => { live = false; };
  }, []);

  function dismissPointer() {
    setPointerSeen(true);
    safeLocalStorage()?.setItem(POINTER_KEY, "1");
  }

  async function choose(file: File | undefined) {
    if (!file) return;
    setBusy(true);
    setNote("");
    dismissPointer();
    const shot = await prepareAvatarForUpload(file, { edge: AVATAR_PHOTO_EDGE });
    if (!shot) {
      setBusy(false);
      // The refusal sentence comes from the shared law with no bytes to weigh, so the member reads
      // the same words the server would have sent her if this had gone up and come back.
      setNote(avatarRefusal({ bytes: 1, contentType: "" }));
      return;
    }
    const refusal = avatarRefusal({ bytes: shot.bytes, contentType: shot.contentType, width: shot.width, height: shot.height });
    if (refusal) { setBusy(false); setNote(refusal); return; }

    // R39's law: READ THE PAYLOAD. This route refuses with a 400, but a refusal that arrived as a
    // 200 would still be a refusal, and a control that reads the status code would show her a photo
    // that is not there. R72 moved the reading of it into `src/lib/avatar-photo.ts`, where a gate
    // RUNS every answer — including the one nobody had thought about, a 500 carrying a raw database
    // error and no `ok`, which this line used to print straight onto her screen.
    const answer = await saveAvatar(shot);
    setBusy(false);
    // A LANDED WRITE IS ALSO A LANDED READ: the payload carries the new URL, so the face and the flag
    // move together and there is no state where one is true and the other is not.
    if (answer.ok) { setPhoto(answer.photo); setLoaded(true); }
    setNote(avatarNote(answer));
  }

  async function remove() {
    setBusy(true);
    const answer = await removeAvatar();
    setBusy(false);
    if (answer.ok) { setPhoto(null); setLoaded(true); }
    setNote(avatarNote(answer));
  }

  return (
    <div className="my-room__photo">
      <span className={photo ? "my-room__face has-photo" : "my-room__face"}>
        {photo ? <img src={photo} alt={AVATAR_ALT} /> : <span aria-hidden="true">{glyph}</span>}
      </span>
      <div className="my-room__photo-actions">
        {/* R72 — BOTH OF THE OLD LABELS ARE CLAIMS, so before the read lands neither is honest and
            the neutral one stands in. She can always choose a photo; what the house may not do is
            tell her she has none while it is still asking, or after an answer that never came. */}
        <button type="button" className="my-room__photo-btn" disabled={busy} onClick={() => fileRef.current?.click()}>
          {!loaded ? AVATAR_CHOOSE_LABEL : photo ? AVATAR_REPLACE_LABEL : AVATAR_UPLOAD_LABEL}
        </button>
        {/* And the way BACK to the glyph is offered only on a read that landed and found a face —
            a remove over an unknown state is a control that destroys something nobody has confirmed
            is there (R35's argument, from the other end). */}
        {loaded && photo ? (
          <button type="button" className="my-room__photo-btn my-room__photo-btn--ghost" disabled={busy} onClick={remove}>
            {AVATAR_REMOVE_LABEL}
          </button>
        ) : null}
      </div>
      <p className="my-room__photo-help" role="status">{busy ? AVATAR_SAVING : note || AVATAR_HELP}</p>
      {/* THE ONE-TIME POINTER, per the house's pointer rules: it shows once, she dismisses it, and
          choosing a photo dismisses it too — a pointer still glowing beside the thing it pointed at
          after she has done it is a pointer that has stopped meaning anything. */}
      {loaded && !photo && !pointerSeen && pointerReady ? (
        <div className="room-pointer room-pointer--photo" role="note">
          <span className="room-pointer__body">
            <span className="room-pointer__text">{AVATAR_POINTER}</span>
            <button type="button" className="room-pointer__dismiss" onClick={dismissPointer} aria-label="Dismiss">Got it</button>
          </span>
        </div>
      ) : null}
      <input
        ref={fileRef}
        type="file"
        className="visually-hidden"
        accept={photoAccept()}
        onChange={(e) => { choose(e.target.files?.[0]); e.target.value = ""; }}
      />
    </div>
  );
}
